import { observer } from 'mobx-react-lite';
import React, { useEffect, useRef, useState } from 'react';
import { TabPanel } from 'react-tabs';
import { colors } from '../../../../../../../../../../colorVars';
import { addExitTactic } from '../../../../../../../../../../stores/websocketStore/utils/outgoingMessages/tradePlan/updateTradePlanMessages/addExitTactic';
import { useWebsocketStore } from '../../../../../../../../../../stores/websocketStore/websocketProvider';
import TextButton from '../../../../../../../../../atomic/buttons/textButton/TextButton';
import { EditableTableFieldNoOnBlurUpdate } from '../../../../../../../../../atomic/inputs/editableTableField/EditableFieldNoOnBlurUpdate';
import { TableSelect } from '../../../../../../../../../atomic/inputs/tableSelect/TableSelect';
import { Body3, fontSizes } from '../../../../../../../../../atomic/text/Text';
import { greenToRedCandlesValidation } from '../../../../../../../../../forms/tradeplans/AddTradePlan/DataValidation';
import { IndividualPanelHeader } from '../../../../individualTacticPanels/IndividualPanelHeader';
import style from './IndividualAddTacticPanel.module.css';

export const AddGreenToRedCandlesTacticPanel = observer(
  // eslint-disable-next-line no-unused-vars
  ({ tradePlanId, softOrderId, addTab, ...rest }) => {
    const { tradePlanStore } = useWebsocketStore();
    const tradePlan = tradePlanStore.tradePlans[tradePlanId];
    const [headerText, setHeaderText] = useState('Add Green To Red Candles');
    const [headerTextColor, setHeaderTextColor] = useState(colors.lightGrey);
    const [details, setDetails] = useState({
      candleDuration: 1,
      timeUnit: 'MINUTES',
      inRightDirectionPct: 0,
      retracePct: 0,
      minCandleHeight: 0,
    });
    const timeoutRef = useRef(null);

    const addTactic = () => {
      const detailsCopy = { ...details };
      detailsCopy.inRightDirectionPct =
        Number(details.inRightDirectionPct) / 100;
      detailsCopy.retracePct = Number(details.retracePct) / 100;
      const validationResult = greenToRedCandlesValidation(details);
      if (validationResult[0] === false) {
        setHeaderText(validationResult[1]);
        setHeaderTextColor(colors.red);
        const timer = setTimeout(() => {
          setHeaderText('Add Green To Red Candles');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      } else {
        addExitTactic(tradePlanId, {
          tactic: 'GREEN_TO_RED_CANDLES',
          details,
        });
        setHeaderText('Green To Red Candles Added');
        setHeaderTextColor(colors.green);
        const timer = setTimeout(() => {
          setHeaderText('Add Green To Red Candles');
          setHeaderTextColor(colors.lightGrey);
        }, 2000);
        timeoutRef.current = timer;
      }
    };
    useEffect(() => {
      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current);
      };
    }, []);
    return (
      <TabPanel {...rest}>
        <div className={style.tableContainer}>
          <table className={style.table}>
            <IndividualPanelHeader />
            <tbody>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Candle Duration
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <EditableTableFieldNoOnBlurUpdate
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                    fontSize={fontSizes.body3}
                    value={details.candleDuration}
                    setValue={(newValue) =>
                      setDetails((prev) => ({
                        ...prev,
                        candleDuration: newValue,
                      }))
                    }
                  />
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Time Unit
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <TableSelect
                    color={colors.lightGrey}
                    backgroundColor="inherit"
                    value={details.timeUnit}
                    options={[
                      { label: 'Minute', value: 'MINUTES' },
                      { label: 'Day', value: 'DAYS' },
                      { label: 'Week', value: 'WEEKS' },
                      { label: 'Month', value: 'MONTHS' },
                      { label: 'Quarter', value: 'QUARTERS' },
                      { label: 'Year', value: 'YEARS' },
                    ]}
                    onChange={(value) =>
                      setDetails((prev) => ({ ...prev, timeUnit: value }))
                    }
                  />
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Right Direction %
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      value={details.inRightDirectionPct}
                      setValue={(newValue) =>
                        setDetails((prev) => ({
                          ...prev,
                          inRightDirectionPct: newValue,
                        }))
                      }
                    />
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      %
                    </Body3>
                  </div>
                </td>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Retrace %
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <EditableTableFieldNoOnBlurUpdate
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      value={details.retracePct}
                      setValue={(newValue) =>
                        setDetails((prev) => ({
                          ...prev,
                          retracePct: newValue,
                        }))
                      }
                    />
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      %
                    </Body3>
                  </div>
                </td>
              </tr>
              <tr>
                <td className={style.labelTd}>
                  <Body3 fontWeight={700} color={colors.lightGrey}>
                    Min. Candle Height
                  </Body3>
                </td>
                <td className={style.inputTd}>
                  <div className={style.inputWrapper}>
                    <Body3
                      fontWeight={700}
                      color={colors.lightGrey}
                      margin="3px 3px 0 0"
                    >
                      $
                    </Body3>
                    <EditableTableFieldNoOnBlurUpdate
                      color={colors.lightGrey}
                      backgroundColor="inherit"
                      fontSize={fontSizes.body3}
                      value={details.minCandleHeight}
                      setValue={(newValue) =>
                        setDetails((prev) => ({
                          ...prev,
                          minCandleHeight: newValue,
                        }))
                      }
                    />
                  </div>
                </td>
                <td></td>
                <td></td>
              </tr>
            </tbody>
          </table>
          <header className={style.header}>
            <Body3 color={headerTextColor} fontWeight={700}>
              {headerText}
            </Body3>
            <TextButton
              disabled={tradePlan.exitTactics['GREEN_TO_RED_CANDLES']}
              color={colors.lightGrey}
              backgroundColor={
                tradePlan.exitTactics['GREEN_TO_RED_CANDLES']
                  ? colors.grey
                  : colors.blue
              }
              fontSize="small"
              clickHandler={addTactic}
              width="20%"
              height="50%"
            >
              Add
            </TextButton>
          </header>
        </div>
      </TabPanel>
    );
  }
);

AddGreenToRedCandlesTacticPanel.tabsRole = 'TabPanel';
